import React, { useEffect } from 'react';
import { styled } from '@compiled/react';
import OriginTracing from '@atlassiansox/origin-tracing';
import Button from '@atlaskit/button';
import { Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useCanUpgradeAndPayForJswFree } from '@atlassian/jira-billing/src/controllers/billing-permissions/index.tsx';
import { useCanUpgradeAndPay } from '@atlassian/jira-billing/src/controllers/can-upgrade-and-pay/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import {
	JswUpgradeButtonOld,
	JswUpgradeButton,
} from '@atlassian/jira-jsw-upgrade-button/src/ui/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { SOFTWARE } from '@atlassian/jira-shared-types/src/application-key.tsx';
import { STANDARD_EDITION } from '@atlassian/jira-shared-types/src/edition.tsx';
import { RolesPickerActionsSkeleton } from '@atlassian/jira-skeletons/src/ui/feature-gates-tmp-access/index.tsx';
import {
	FEATURE_GATE_LEARN_MORE_ACTION_SUBJECT,
	FEATURE_GATE_UPGRADE_ACTION_SUBJECT,
	LEARN_MORE_URL,
	PACKAGE_NAME,
	USER_ROLES_PICKER_TOUCHPOINT_ID,
} from '../../../common/constants';
import messages from './messages';

export const COMPONENT_NAME = 'FeatureGatedUserRolesPicker';

type Props = {
	intl: IntlShape;
};

const DropdownFooter = (props: Props) => {
	const {
		intl: { formatMessage },
	} = props;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isEnhancedFeatureGatesProductionized = fg(
		'jsw_enhanced_feature_gates_productionisation_gate',
	);
	const {
		fetch: fetchCanUpgradeAndPay,
		isLoading: canUpgradeAndPayLoading,
		data: canUpgradeAndPayData,
		error: useCanUpgradeAndPayError,
	} = useCanUpgradeAndPay({
		applicationKey: SOFTWARE,
		edition: STANDARD_EDITION,
	});
	const {
		canUpgradeAndPay: canUpgradeAndPayForJswFree,
		loading: canUpgradeAndPayForJswFreeLoading,
	} = useCanUpgradeAndPayForJswFree({
		billingAdminCheckMethod: 'ccpApi',
		caller: COMPONENT_NAME,
		skip: isEnhancedFeatureGatesProductionized,
	});
	const canUpgradeAndPay = canUpgradeAndPayData && canUpgradeAndPayData.canUpgradeAndPay;

	const origin = new OriginTracing({ product: 'jira' });
	const billingPageUrl =
		canUpgradeAndPayData?.upgradeLink && origin.addToUrl(canUpgradeAndPayData.upgradeLink);

	const shouldShowOldUpgradeButton =
		!isEnhancedFeatureGatesProductionized && canUpgradeAndPayForJswFree;
	const shouldShowUpgradeButton =
		isEnhancedFeatureGatesProductionized && canUpgradeAndPay && billingPageUrl;
	const isPermissionsLoading = isEnhancedFeatureGatesProductionized
		? canUpgradeAndPayLoading
		: canUpgradeAndPayForJswFreeLoading;

	useEffect(() => {
		if (isEnhancedFeatureGatesProductionized) {
			fetchCanUpgradeAndPay();
		}
	}, [isEnhancedFeatureGatesProductionized, fetchCanUpgradeAndPay]);

	useEffect(() => {
		if (useCanUpgradeAndPayError) {
			fireErrorAnalytics({
				error: useCanUpgradeAndPayError,
				meta: {
					id: 'useCanUpgradeAndPay',
					packageName: PACKAGE_NAME,
					teamName: 'growth-tako',
				},
				attributes: {
					caller: COMPONENT_NAME,
					touchpoint: USER_ROLES_PICKER_TOUCHPOINT_ID,
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	}, [useCanUpgradeAndPayError]);

	const handleUpgradeButtonClick = () =>
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: FEATURE_GATE_UPGRADE_ACTION_SUBJECT,
				action: 'clicked',
			}),
			{
				touchpoint: USER_ROLES_PICKER_TOUCHPOINT_ID,
			},
		);

	const handleLearnMoreLinkClick = () =>
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: FEATURE_GATE_LEARN_MORE_ACTION_SUBJECT,
				action: 'clicked',
			}),
			{
				touchpoint: USER_ROLES_PICKER_TOUCHPOINT_ID,
			},
		);

	return (
		<Container>
			<Text size="UNSAFE_small" as="p">
				{formatMessage(messages.userRoleFooterText)}
			</Text>
			{isPermissionsLoading ? (
				<RolesPickerActionsSkeleton />
			) : (
				<FooterActions>
					{shouldShowOldUpgradeButton && (
						<JswUpgradeButtonOld
							caller={COMPONENT_NAME}
							editionToUpgradeTo={STANDARD_EDITION}
							onClick={handleUpgradeButtonClick}
						/>
					)}
					{shouldShowUpgradeButton && (
						<JswUpgradeButton
							testId="feature-gates-tmp-access.ui.feature-gated-roles-picker.footer.jsw-upgrade-button"
							onClick={handleUpgradeButtonClick}
							billingPageUrl={billingPageUrl}
						/>
					)}
					<Button
						// We don't want any padding around the link if there's no "Upgrade" button next to it,
						// otherwise it'll be misaligned with the rest of the footer content.
						spacing={shouldShowOldUpgradeButton || shouldShowUpgradeButton ? 'default' : 'none'}
						appearance="link"
						href={LEARN_MORE_URL}
						target="_blank"
						onClick={handleLearnMoreLinkClick}
					>
						{formatMessage(messages.learnMore)}
					</Button>
				</FooterActions>
			)}
		</Container>
	);
};

export default injectIntl(DropdownFooter);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	flexDirection: 'column',
	padding: `${token('space.200', '16px')} ${token('space.250', '20px')}`,
	borderTop: `2px solid ${token('color.border', '#091E4224')}`,
	color: token('color.text', '#172B4D'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterActions = styled.div({
	display: 'flex',
	marginTop: token('space.150', '12px'),
	gap: token('space.100', '8px'),
});
